import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { SearchInputAdornment } from '@components/SearchInputAdornment';
import { TelefoneField } from '@components/Fields/TelefoneField';
import { IForm } from '@hooks/useForm';
import useFormatter from '@hooks/useFormatter';
import { ILoading } from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { IValueState } from '@hooks/useValueState';
import { makeStyles } from '@material-ui/core';
import ILinha from '@model/Linha';
import { IValidarNumeroResponse, useContratoClienteService } from '@services/ContratoClienteService';
import { useThemeColor } from '@themes/Colors';
import clsx from 'clsx';
import React from 'react';
import { SuccessMessage } from '../SuccessMessage';
import { WarningMessage } from '../WarningMessage';

export type LinhaSearchProps = {
    linhaValueState: IValueState<IValidarNumeroResponse>
    loading: ILoading
    form: IForm<Partial<ILinha>>
    telefoneFieldRef: React.MutableRefObject<HTMLInputElement | undefined>    
    modal: IModalWindow
}

export function LinhaSearch ({ loading, linhaValueState, form, telefoneFieldRef, modal }: LinhaSearchProps) {
    const themeColor = useThemeColor();
    const cls = useStyles();
    const formatter = useFormatter();
    const contratoClienteService = useContratoClienteService();
    const message = useMessage();

    const searched = linhaValueState.value !== undefined && linhaValueState.value?.linha !== null;
    const linhaFound = !!linhaValueState.value?.linha;
    const contratoClienteFound = !!linhaValueState.value?.contratoCliente;

    const handleSearch = React.useCallback((numero?: string) => {
        if (!loading.isLoading) {
            const value = formatter.extractNumbers(form.fields.numeroSemMascara.getInputValue());

            if (formatter.isValidCelularLength(value)) {
                loading.start();
                contratoClienteService.validarNumero(value)
                    .then(response => {
                        linhaValueState.set(response.data);

                        if (response.data.linha !== undefined) {
                            if (response.data.contratoCliente !== undefined) {
                                modal.open();                            
                            }
                            else {
                                form.setValues(response.data.linha!);
                            }
                        }
                    })
                    .catch(message.showException)
                    .finally(loading.stop);
            }
        }
    }, [loading, formatter, form, contratoClienteService, message.showException, linhaValueState, modal]);

    return (
        <HorizontalGrid verticalAlign="center" className={clsx({
            [cls.stepContainer]: true,
            [themeColor.component.successPanel]: searched && linhaFound && !contratoClienteFound,
            [themeColor.component.warningPanel]: searched && !linhaFound && !contratoClienteFound,
        })}>
            <GridItem>
                <TelefoneField 
                    field={form.fields.numeroSemMascara}
                    inputRef={telefoneFieldRef}
                    label="Número"
                    maskType="cel"
                    required
                    disabled={searched}
                    updateFieldStateOnChange
                    InputProps={searched ? {} : {
                        endAdornment: <SearchInputAdornment onButtonClick={handleSearch} disabled={!formatter.isValidCelularLength(form.fields.numeroSemMascara.value)} />
                    }}
                />
            </GridItem>

            {
                searched && !contratoClienteFound && (
                    <GridItem>
                        {
                            linhaFound
                            ? (
                                <SuccessMessage
                                    message="Linha encontrada e disponível para contratação" 
                                />
                            )
                            : (
                                <WarningMessage
                                    message="Linha não encontrada, é necessário cadastrá-la" 
                                />
                            )
                        }
                    </GridItem>
                )
            }
        </HorizontalGrid>        
    )
}

const useStyles = makeStyles(theme => ({
    stepContainer: {
        padding: `${theme.spacing(1)}px ${theme.spacing(4)}px ${theme.spacing(1)}px ${theme.spacing(4)}px`
    }
}));